import type { Location, LocationDescriptor } from "history";
import { Component, cloneElement } from "react";
import { Route } from "react-router";
import { push } from "react-router-redux";

import { connect } from "metabase/redux";
import { Modal, type ModalProps } from "metabase/ui";
import MetabaseSettings from "metabase/utils/settings";

type RouteParams = Record<string, string | undefined>;

type IRoute = {
  path: string;
};

export const getParentPath = (route: IRoute, location: Location) => {
  // If instance has a custom url we need to exclude its subpath
  const siteUrlSegments = (MetabaseSettings.get("site-url") ?? "").split("/");
  const subPath = siteUrlSegments.slice(3).join("/");

  let pathName: string;
  if (subPath) {
    const subPathSplit = location.pathname.split(subPath);

    pathName =
      subPathSplit.length === 1
        ? subPathSplit[0]
        : subPathSplit.slice(1).join(subPath);
  } else {
    pathName = location.pathname;
  }

  const fullPathSegments = pathName.split("/");
  const routeSegments = route.path.split("/");

  fullPathSegments.splice(-routeSegments.length);

  return fullPathSegments.join("/");
};

export type ComposedModalProps<
  P extends RouteParams = RouteParams,
  Q = unknown,
> = {
  params: P;
  location: Location<Q>;
  onClose: () => void;
};

interface WrappedModalRouteProps {
  route: IRoute;
  params: RouteParams;
  location: Location;
  onChangeLocation: (nextLocation: LocationDescriptor) => void;
}

const ModalWithRoute = (
  ComposedModal: React.ComponentType<ComposedModalProps>,
  modalProps: Partial<ModalProps> = {},
  noWrap = false,
) => {
  class ModalRouteComponent extends Component<WrappedModalRouteProps> {
    static displayName: string = `ModalWithRoute[${
      ComposedModal.displayName || ComposedModal.name
    }]`;

    // Whatever had focus right before this route-modal mounted (typically
    // the control that navigated here, e.g. a menu item or link), so it can
    // be restored once the modal unmounts again. This component's own
    // mount/unmount cycle IS the modal's open/close cycle — the wrapped
    // <Modal>'s `opened` prop (below, and in every ComposedModal that
    // renders its own <Modal opened .../> under `noWrap`) never itself
    // transitions false->true, since the component only ever exists while
    // "open." That means Mantine's own built-in focus-return, which relies
    // on observing a real prop transition, never actually fires here —
    // without this, closing a route-modal silently drops focus to <body>
    // (WCAG 2.4.3).
    triggerElement: HTMLElement | null = null;

    // Tracks the trigger continuously rather than sampling
    // `document.activeElement` once at a guessed delay. The control that
    // navigated here is very often itself inside a closing Menu/Popover
    // (e.g. an "Edit" item), whose own focus-return effect — if that menu
    // has one — can take a moment to settle, and can pass through
    // transient intermediate elements (portal/focus-trap internals) on
    // the way. A single timed sample risks catching one of those
    // transients instead of the real, final resting focus. Listening for
    // every focus change and always keeping the latest one outside this
    // modal's own dialog self-corrects regardless of how many
    // intermediate hops happen, without needing to guess a delay at all.
    handleFocusIn = () => {
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLElement &&
        activeElement !== document.body &&
        // Exclude focus landing inside this modal's own dialog (e.g. its
        // Cancel button, right as it's clicked) — that's never a
        // meaningful "trigger to restore to," and capturing it would
        // point back at an element that's about to be removed anyway.
        !activeElement.closest('[role="dialog"]')
      ) {
        this.triggerElement = activeElement;
      }
    };

    componentDidMount() {
      document.addEventListener("focusin", this.handleFocusIn);
      // Also capture synchronously in case whatever triggered this
      // navigation already has focus and never fires its own focusin (a
      // plain link/button click that isn't inside a closing menu).
      this.handleFocusIn();
    }

    componentWillUnmount() {
      document.removeEventListener("focusin", this.handleFocusIn);
      const triggerElement = this.triggerElement;
      // Deferred, matching Mantine's own useFocusReturn (which restores
      // focus after a short delay for exactly this reason): gives whatever
      // else is happening in this same navigation (e.g. the destination
      // page settling in) a chance to claim focus first, so this doesn't
      // fight it or act on stale information about what currently has
      // focus.
      window.setTimeout(() => {
        const activeElement = document.activeElement;
        const nothingElseIsFocused =
          activeElement === document.body || activeElement == null;

        if (nothingElseIsFocused && triggerElement?.isConnected) {
          triggerElement.focus();
        }
      }, 10);
    }

    onClose = () => {
      const { location, route } = this.props;

      const parentPath = getParentPath(route, location);
      this.props.onChangeLocation(parentPath);
    };

    render() {
      if (noWrap) {
        return <ComposedModal {...this.props} onClose={this.onClose} />;
      }

      return (
        <Modal
          opened
          onClose={this.onClose}
          withCloseButton={false}
          padding={0}
          size="lg"
          {...modalProps}
        >
          <ComposedModal {...this.props} onClose={this.onClose} />
        </Modal>
      );
    }
  }

  return connect(null, { onChangeLocation: push })(ModalRouteComponent);
};

// Base props that any modal rendered by ModalRoute must accept.
// Modal components typically narrow `params` to specific keys (e.g., { alertId?: string }),
// but they must accept the full ComposedModalProps shape.
export type ModalComponentProps = {
  params: RouteParams;
  location: Location;
  onClose: () => void;
};

interface ModalRouteProps {
  path: string;
  modal: React.ComponentType<ModalComponentProps>;
  modalProps?: Partial<ModalProps>;
  noWrap?: boolean;
}

// react-router Route wrapper that handles routed modals
class _ModalRoute extends Route {
  static createRouteFromReactElement(element: React.ReactElement) {
    const { modal, modalProps, noWrap } = element.props;

    if (modal) {
      element = cloneElement(element, {
        component: ModalWithRoute(modal, modalProps, noWrap),
      });

      // @ts-expect-error - Route.createRouteFromReactElement is not typed
      return Route.createRouteFromReactElement(element);
    } else {
      throw new Error("`modal` property is missing from ModalRoute");
    }
  }
}

// Casting ModalRoute as there's no way to properly type its props
// ModalRoute extends react-router's Route which is not generic,
// so it's impossible to extend Route's props.
export const ModalRoute =
  _ModalRoute as unknown as React.ComponentType<ModalRouteProps>;
